import { FC, FormEvent, useState } from 'react';
import { Button } from '@/components/ui';
import api from '@/utils/api';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/utils/error';

interface SmtpConfigFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const SmtpConfigForm: FC<SmtpConfigFormProps> = ({ onSuccess, onCancel }) => {
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [secure, setSecure] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async () => {
    setError('');
    setSuccess('');

    if (!host || !port || !username || !password) {
      setError('All fields are required for verification');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await api.post('/email-config/verify', {
        providerType: 'smtp',
        isActive: true, // This is just a dummy value for the DTO
        smtpHost: host,
        smtpPort: parseInt(port),
        smtpUsername: username,
        smtpPassword: password,
        smtpSecure: secure,
      });

      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!host || !port || !username || !password) {
      setError('All fields are required');
      return;
    }

    try {
      setIsSaving(true);
      await api.post('/email-config', {
        providerType: 'smtp',
        isActive: true,
        smtpHost: host,
        smtpPort: parseInt(port),
        smtpUsername: username,
        smtpPassword: password,
        smtpSecure: secure,
      });
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Alert Banners */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-2 text-green-700 text-sm">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2">Server Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Host <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.gmail.com"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Port <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              placeholder="587"
              min="1"
              max="65535"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
              disabled={isSaving}
            />
          </div>
        </div>

        <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2 pt-2">Authentication</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your@email.com"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            id="secure"
            checked={secure}
            onChange={(e) => setSecure(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <label htmlFor="secure" className="text-sm text-gray-700 cursor-pointer select-none">
            Use SSL/TLS (Recommended)
          </label>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving || isVerifying}
          className="text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={handleVerify}
          disabled={isSaving || isVerifying || !host || !port || !username || !password}
          className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            'Check Connection'
          )}
        </Button>

        <Button
          type="submit"
          variant="primary"
          isLoading={isSaving}
          disabled={isSaving || isVerifying}
          className="bg-gray-900 text-white hover:bg-black"
        >
          Save Configuration
        </Button>
      </div>
    </form>
  );
};
